// Tiny typed fetch wrapper. Cookies are sent automatically (SameSite=Strict).

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    // Parsed JSON error body, when one was sent. Carries auxiliary fields
    // beyond `error` — e.g. `missing` items for boilerplate failures,
    // `decisionCount` for "not pristine" PATCH refusals on admin prompts.
    public readonly body: Record<string, unknown> | null = null,
  ) {
    super(message);
  }
}

async function readErrorBody(
  res: Response,
): Promise<{ detail: string; body: Record<string, unknown> | null }> {
  let detail = res.statusText;
  try {
    const parsed = (await res.json()) as Record<string, unknown>;
    if (typeof parsed.error === 'string') detail = parsed.error;
    return { detail, body: parsed };
  } catch {
    return { detail, body: null };
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'include' };
  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (!res.ok) {
    const { detail, body: errBody } = await readErrorBody(res);
    throw new ApiError(res.status, detail, errBody);
  }
  // 204 No Content (e.g. the resource-access recorder) has an empty body —
  // `res.json()` would throw "Unexpected end of JSON input". Callers that
  // expect no payload type this as `void`.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestForm<T>(method: string, path: string, form: FormData): Promise<T> {
  // Don't set Content-Type — the browser adds the multipart boundary itself.
  const res = await fetch(path, { method, credentials: 'include', body: form });
  if (!res.ok) {
    const { detail, body: errBody } = await readErrorBody(res);
    throw new ApiError(res.status, detail, errBody);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  postForm: <T>(path: string, form: FormData) => requestForm<T>('POST', path, form),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  // Slice 31 — `put` for the governance upsert endpoints. Same shape as
  // `post`; the verb distinction matches REST semantics for idempotent
  // replace-or-create writes on a known resource key.
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
